import type { InstallationConfig } from '@/api/installation.api';
import type { ItemDeSeguimiento, Seguimientos } from '@/api/seguimientos.api';

/**
 * AGENDA DE ZOOM EN EL PANEL (tanda 3, ola C). PURO.
 *  - Las citas como bloques del calendario de `Agenda.tsx`, que pinta lo mismo que
 *    pinta para el Inspector (id, fecha, descripción, cajón, si es suposición, contacto).
 *  - El formulario de la agenda en «Ajustes del agente».
 */

/** Un Zoom como bloque del calendario. */
export interface BloqueDeZoom {
  id: string;
  fechaCompromiso: string;
  descripcion: string;
  cajon: 'CLIENTES';
  /** Una cita que eligió el cliente no es una suposición: sale rellena, no punteada. */
  esSuposicion: false;
  /** `Agenda.tsx` marca choques entre gente distinta por este campo: el teléfono sirve igual. */
  contactId: string;
  conversationId: string | null;
  conQuien: string;
  telefono: string | null;
}

/** Todas las citas abiertas, en orden, sin importar en qué grupo las puso la API. */
export function zoomsComoBloques(s: Seguimientos | undefined): BloqueDeZoom[] {
  if (!s) return [];
  const todos: ItemDeSeguimiento[] = [...s.vencidos, ...s.hoy, ...s.estaSemana, ...s.masAdelante, ...s.paraAprobar];
  return todos
    .map((i) => ({
      id: i.id,
      fechaCompromiso: i.cuando,
      descripcion: i.que,
      cajon: 'CLIENTES' as const,
      esSuposicion: false as const,
      contactId: i.telefono ?? i.id,
      conversationId: i.conversationId,
      conQuien: i.conQuien,
      telefono: i.telefono,
    }))
    .sort((a, b) => Date.parse(a.fechaCompromiso) - Date.parse(b.fechaCompromiso));
}

/** Los días, de lunes a domingo, como se ven en Ajustes. `n` es el de la API (0 = domingo). */
export const DIAS_DE_LA_SEMANA: ReadonlyArray<{ n: number; corto: string }> = [
  { n: 1, corto: 'Lun' },
  { n: 2, corto: 'Mar' },
  { n: 3, corto: 'Mié' },
  { n: 4, corto: 'Jue' },
  { n: 5, corto: 'Vie' },
  { n: 6, corto: 'Sáb' },
  { n: 0, corto: 'Dom' },
];

export interface AgendaForm {
  activa: boolean;
  zoomUrl: string;
  dias: number[];
  /** Las horas como las escribe Richard: «11:00, 16:00». */
  horas: string;
  duracionMin: string;
}

const HORA = /^([01]?\d|2[0-3]):[0-5]\d$/;
const horasDe = (texto: string): string[] => texto.split(/[,\s]+/).map((h) => h.trim()).filter(Boolean);

export function agendaDesdeConfig(cfg: InstallationConfig): AgendaForm {
  const a = cfg.crm.agenda ?? null;
  return a
    ? { activa: true, zoomUrl: a.zoomUrl, dias: [...a.dias], horas: a.horas.join(', '), duracionMin: String(a.duracionMin) }
    : { activa: false, zoomUrl: '', dias: [1, 2, 3, 4, 5], horas: '', duracionMin: '45' };
}

/** Errores por campo. Vacío = se puede guardar. Apagada no se valida. */
export function erroresDeAgenda(f: AgendaForm): Record<string, string> {
  if (!f.activa) return {};
  const e: Record<string, string> = {};
  const url = f.zoomUrl.trim();
  if (url && !/^https:\/\/\S+$/.test(url)) e.zoomUrl = 'Pega el enlace completo de tu sala de Zoom, empezando por https://';
  if (!f.dias.length) e.dias = 'Elige al menos un día.';
  const horas = horasDe(f.horas);
  if (!horas.length || horas.some((h) => !HORA.test(h))) e.horas = 'Escribe las horas en formato de 24 h, separadas por comas (11:00, 16:00).';
  const d = Number(f.duracionMin);
  if (!Number.isInteger(d) || d < 15 || d > 240) e.duracionMin = 'Entre 15 y 240 minutos.';
  return e;
}

/** El PUT con la agenda puesta (o apagada, `null`). Lo demás va tal cual. */
export function conAgenda<P extends { crm: object }>(payload: P, f: AgendaForm): P {
  const agenda = f.activa
    ? {
        zoomUrl: f.zoomUrl.trim(),
        dias: [...f.dias].sort((a, b) => a - b),
        horas: horasDe(f.horas).map((h) => h.padStart(5, '0')).sort(),
        duracionMin: Number(f.duracionMin),
      }
    : null;
  return { ...payload, crm: { ...payload.crm, agenda } } as P;
}
